/**
 * Upload History Composable
 * Business logic and helper functions for Upload History
 */

import {
  UPLOAD_STATUS,
  STATUS_COLORS,
  STATUS_ICONS,
  DURATION_THRESHOLDS,
  DURATION_COLORS,
  FILE_SIZE,
  TEMPLATE_TYPES,
} from '@/constants/uploadHistory';

export const useUploadHistory = () => {
  /**
   * Get status display text
   * @param {string} status
   * @returns {string}
   */
  const getStatusText = (status) => {
    const statusMap = {
      [UPLOAD_STATUS.PROCESSING]: 'กำลังประมวลผล',
      [UPLOAD_STATUS.COMPLETED]: 'สำเร็จ',
      [UPLOAD_STATUS.COMPLETED_WITH_ERRORS]: 'สำเร็จบางส่วน',
      [UPLOAD_STATUS.FAILED]: 'ล้มเหลว',
    };
    return statusMap[status] || status;
  };

  /**
   * Get status color
   * @param {string} status
   * @returns {string}
   */
  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.DEFAULT;
  };

  /**
   * Get status icon
   * @param {string} status
   * @returns {string}
   */
  const getStatusIcon = (status) => {
    return STATUS_ICONS[status] || STATUS_ICONS.DEFAULT;
  };

  /**
   * Get file icon color based on file size
   * @param {Object} fileInfo
   * @returns {string}
   */
  const getFileIconColor = (fileInfo) => {
    if (!fileInfo) return 'grey';
    const size = fileInfo.size || 0;
    if (size === FILE_SIZE.ZERO) return 'error';
    if (size > FILE_SIZE.LARGE_FILE_THRESHOLD) return 'warning';
    return 'primary';
  };

  /**
   * Get duration color based on performance thresholds
   * @param {number} duration - Duration in seconds
   * @returns {string}
   */
  const getDurationColor = (duration) => {
    if (duration < DURATION_THRESHOLDS.VERY_FAST) {
      return DURATION_COLORS.VERY_FAST;
    }
    if (duration < DURATION_THRESHOLDS.NORMAL) {
      return DURATION_COLORS.NORMAL;
    }
    if (duration < DURATION_THRESHOLDS.SLOW) {
      return DURATION_COLORS.SLOW;
    }
    return DURATION_COLORS.VERY_SLOW;
  };

  /**
   * Get template display name
   * @param {string} templateType
   * @returns {string}
   */
  const getTemplateName = (templateType) => {
    return templateType === TEMPLATE_TYPES.TEMPLATE_1 ? 'Template 1' : 'Template 2';
  };

  /**
   * Calculate statistics from filtered history
   * @param {Array} history
   * @returns {Object|null}
   */
  const calculateStatistics = (history) => {
    if (!history || history.length === 0) {
      return null;
    }

    const totalUploads = history.length;
    const successfulUploads = history.filter(
      (log) => log.status === UPLOAD_STATUS.COMPLETED
    ).length;
    const partialUploads = history.filter(
      (log) => log.status === UPLOAD_STATUS.COMPLETED_WITH_ERRORS
    ).length;
    const failedUploads = history.filter(
      (log) => log.status === UPLOAD_STATUS.FAILED
    ).length;

    return {
      totalUploads,
      successfulUploads,
      partialUploads,
      failedUploads,
    };
  };

  /**
   * Calculate success rate percentage
   * @param {Object} statistics
   * @returns {number}
   */
  const getSuccessRate = (statistics) => {
    if (!statistics || statistics.totalUploads === 0) return 0;
    return Math.round((statistics.successfulUploads / statistics.totalUploads) * 100);
  };

  /**
   * Calculate partial success rate percentage
   * @param {Object} statistics
   * @returns {number}
   */
  const getPartialRate = (statistics) => {
    if (!statistics || statistics.totalUploads === 0) return 0;
    return Math.round((statistics.partialUploads / statistics.totalUploads) * 100);
  };

  /**
   * Calculate failure rate percentage
   * @param {Object} statistics
   * @returns {number}
   */
  const getFailedRate = (statistics) => {
    if (!statistics || statistics.totalUploads === 0) return 0;
    return Math.round((statistics.failedUploads / statistics.totalUploads) * 100);
  };

  /**
   * Filter history by search term
   * @param {Array} history
   * @param {string} searchTerm
   * @returns {Array}
   */
  const filterBySearch = (history, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return history;

    const searchLower = searchTerm.toLowerCase();
    return history.filter((log) => {
      const fileName = log.fileInfo?.name?.toLowerCase() || '';
      const uploadedBy = log.uploadedBy?.toLowerCase() || '';
      
      // ค้นหาใน failedRecords
      const hasFailedDoc = log.failedRecords?.some((r) =>
        r.docID?.toLowerCase().includes(searchLower)
      );
      
      // ค้นหาใน successRecords
      const hasSuccessDoc = log.successRecords?.some((r) =>
        r.docID?.toLowerCase().includes(searchLower)
      );

      return (
        fileName.includes(searchLower) ||
        uploadedBy.includes(searchLower) ||
        hasFailedDoc ||
        hasSuccessDoc
      );
    });
  };

  /**
   * Filter history by status
   * @param {Array} history
   * @param {string} status
   * @returns {Array}
   */
  const filterByStatus = (history, status) => {
    if (!status) return history;
    return history.filter((log) => log.status === status);
  };

  /**
   * Filter history by template type
   * @param {Array} history
   * @param {string} templateType
   * @returns {Array}
   */
  const filterByTemplate = (history, templateType) => {
    if (!templateType) return history;
    return history.filter((log) => log.templateType === templateType);
  };

  /**
   * Filter history by date
   * @param {Array} history
   * @param {string} dateStr
   * @param {Function} parseTimestamp - Date parser function
   * @returns {Array}
   */
  const filterByDate = (history, dateStr, parseTimestamp) => {
    if (!dateStr) return history;

    const filterDateObj = new Date(dateStr);
    return history.filter((log) => {
      if (!log.uploadedAt) return false;

      const logDate = parseTimestamp(log.uploadedAt);
      if (!logDate) return false;

      return logDate.toDateString() === filterDateObj.toDateString();
    });
  };

  /**
   * Apply all filters to history
   * @param {Array} history
   * @param {Object} filters
   * @param {Function} parseTimestamp
   * @returns {Array}
   */
  const applyAllFilters = (history, filters, parseTimestamp) => {
    let filtered = [...history];

    if (filters.search) {
      filtered = filterBySearch(filtered, filters.search);
    }

    if (filters.status) {
      filtered = filterByStatus(filtered, filters.status);
    }

    if (filters.template) {
      filtered = filterByTemplate(filtered, filters.template);
    }

    if (filters.date) {
      filtered = filterByDate(filtered, filters.date, parseTimestamp);
    }

    return filtered;
  };

  /**
   * Check if any filters are active
   * @param {Object} filters
   * @returns {boolean}
   */
  const hasActiveFilters = (filters) => {
    return !!(
      filters.search ||
      filters.status ||
      filters.template ||
      filters.date
    );
  };

  /**
   * Clear all filters
   * @returns {Object}
   */
  const getClearedFilters = () => {
    return {
      search: '',
      status: null,
      template: null,
      date: null,
    };
  };

  return {
    // Status helpers
    getStatusText,
    getStatusColor,
    getStatusIcon,

    // File helpers
    getFileIconColor,

    // Duration helpers
    getDurationColor,

    // Template helpers
    getTemplateName,

    // Statistics
    calculateStatistics,
    getSuccessRate,
    getPartialRate,
    getFailedRate,

    // Filtering
    filterBySearch,
    filterByStatus,
    filterByTemplate,
    filterByDate,
    applyAllFilters,
    hasActiveFilters,
    getClearedFilters,
  };
};

export default useUploadHistory;
